import { useReducer, useState, useEffect } from 'react'
import classnames from 'classnames'
import Router from 'next/router'
import { APIErrors } from '../../../api-client/enums'
import { formatPhoneNumber } from '../../../utils/format-phone-number'
import { toCapitalize } from '../../../utils/to-capitalize'
import { FormField } from '../../../components/inputs'
import { Button } from '../../../components/button'
import useValidation from '../../../hooks/use-validation'
import { ROUTES } from '../../../constants'

const re = {
  email: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
  password: /^(?=^.{8,}$)((?!.*\s)(?=.*[a-z]))((?=(.*\d){1,})|(?=(.*\W){1,}))^.*$/i,
  phoneNumber: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
  zipCode: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
}

const FormFields = {
  FirstName: 'firstName',
  LastName: 'lastName',
  Email: 'email',
  PhoneNumber: 'phoneNumber',
  ZipCode: 'zipCode',
  Password: 'password',
  ConfirmPassword: 'confirmPassword',
}

const validationErrors = {
  email: 'Email is not valid',
  phoneNumber: 'Mobile Phone number must be 10 digits long',
  password: "Password doesn't meet strength requirements",
  confirmPassword: "Passwords doesn't match",
  zipCode: 'Please enter a valid postal code',
}

const generateUserObject = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  zipCode,
  password,
  confirmPassword,
}) => {
  return {
    firstName: toCapitalize(firstName.value),
    lastName: toCapitalize(lastName.value),
    email: email.value,
    phoneNumber: phoneNumber.value.replace(/[^\d]/g, ''),
    zipCode: zipCode.value,
    password: password.value,
    confirmPassword: confirmPassword.value,
  }
}

const isFieldValid = (field, state) => re[field].test(state[field].value)

const isFieldEmpty = (entry) => (entry.value === '' ? true : false)

const reducer = (state, action) => ({
  ...state,
  [action.payload.field]: {
    ...state[action.payload.field],
    value: action.payload.value,
  },
})

const initialState = {
  firstName: { value: '' },
  lastName: { value: '' },
  email: { value: '' },
  phoneNumber: { value: '' },
  zipCode: { value: '' },
  password: { value: '' },
  confirmPassword: { value: '' },
}

export default function Register({
  onSubmit,
  disabled,
  serverErrors,
  onLogin,
  submittedForm,
  onClose,
}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)
  const [mappedServerErrors, setMappedServerErrors] = useState(() =>
    mapServerErrors(serverErrors?.errors),
  )

  useEffect(() => setMappedServerErrors(mapServerErrors(serverErrors?.errors)), [serverErrors])

  const handleChange = ({ target: { name, value } }) => {
    dispatch({ payload: { field: name, value } })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!isFormValid) showErrors()
    else onSubmit(generateUserObject(state))
  }

  const checkFieldForErrors = (field) => {
    return errors[field] ||
      (mappedServerErrors[field] && submittedForm[field] === state[field].value)
      ? true
      : false
  }

  return (
    <form className='wrapper' onSubmit={handleSubmit}>
      <div className='flex alignCenter flexDirectionColumn'>
        <h2 className='title'>Create your account</h2>
        <p className='loginInstead'>
          Already have an account?{' '}
          <button type='button' className='flatBtn' onClick={onLogin}>
            Sign in
          </button>
        </p>
      </div>
      <div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.firstName },
              { visible: errors.firstName },
            )}
            htmlFor='firstName'
          >
            {errors.firstName}
          </label>
          <FormField
            placeholder='First Name'
            type='text'
            id='firstName'
            name={FormFields.FirstName}
            value={state.firstName.value}
            error={errors.firstName}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.lastName },
              { visible: errors.lastName },
            )}
            htmlFor='lastName'
          >
            {errors.lastName}
          </label>
          <FormField
            placeholder='Last Name'
            type='text'
            id='lastName'
            name={FormFields.LastName}
            value={state.lastName.value}
            error={errors.lastName}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.Email) },
              { visible: checkFieldForErrors(FormFields.Email) },
            )}
            htmlFor='lastName'
          >
            {errors.email || mappedServerErrors.email}
          </label>
          <FormField
            placeholder='Email'
            type='email'
            id='email'
            name={FormFields.Email}
            value={state.email.value}
            error={checkFieldForErrors(FormFields.Email)}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.PhoneNumber) },
              { visible: checkFieldForErrors(FormFields.PhoneNumber) },
            )}
            htmlFor='phoneNumber'
          >
            {errors.phoneNumber || mappedServerErrors.phoneNumber}
          </label>
          <FormField
            placeholder='Mobile Phone'
            type='tel'
            id='phoneNumber'
            inputMode='numeric'
            maxLength='12'
            name={FormFields.PhoneNumber}
            value={formatPhoneNumber(state.phoneNumber.value)}
            error={checkFieldForErrors(FormFields.PhoneNumber)}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.ZipCode) },
              { visible: checkFieldForErrors(FormFields.ZipCode) },
            )}
            htmlFor='zipCode'
          >
            {mappedServerErrors.zipCode || errors.zipCode}
          </label>
          <FormField
            placeholder='Postal code'
            id='zipCode'
            inputMode='numeric'
            name={FormFields.ZipCode}
            value={state.zipCode.value}
            error={mappedServerErrors.zipCode || errors.zipCode}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.password },
              { visible: errors.password },
            )}
            htmlFor='password'
          >
            {errors.password}
          </label>
          <FormField
            placeholder='Password'
            type='password'
            id='password'
            name={FormFields.Password}
            value={state.password.value}
            error={errors.password}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.confirmPassword },
              { visible: errors.confirmPassword },
            )}
            htmlFor='confirmPassword'
          >
            {errors.confirmPassword}
          </label>
          <FormField
            placeholder='Confirm Password'
            type='password'
            id='confirmPassword'
            name={FormFields.ConfirmPassword}
            value={state.confirmPassword.value}
            error={errors?.confirmPassword}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className='flex flexDirectionColumn alignCenter'>
        <p className='passwordRequirements'>
          Password must be a minimum of 8 characters, contain a letter AND a number OR special
          character.
        </p>
        <p className='terms'>
          View our{' '}
          <button
            onClick={() => {
              onClose()
              Router.push(ROUTES.Terms).then(() => window.scrollTo(0, 0))
            }}
            type='button'
            className='flatBtn'
          >
            Terms and Conditions
          </button>{' '}
          or{' '}
          <button
            onClick={() => {
              onClose()
              Router.push(ROUTES.Privacy).then(() => window.scrollTo(0, 0))
            }}
            type='button'
            className='flatBtn'
          >
            Privacy Policy.
          </button>
        </p>
        <label
          className={classnames(
            'label',
            { error: errors.general || mappedServerErrors.general },
            { visible: errors.general || mappedServerErrors.general },
          )}
        >
          {errors.general || mappedServerErrors.general}
        </label>

        <Button type='submit' className='flatbtn signup' disabled={disabled}>
          Sign Up
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 2rem 4.1rem;
          background: #fff;
        }

        .title {
          padding: 0 0 0.8rem;
          line-height: 3.6rem;
          font-size: 3.2rem;
        }

        .label {
          display: none;
          margin: 0 0 1rem;
          color: var(--secondary-text-color);
          font-size: 1.4rem;

          &.visible {
            display: inline-block;
          }

          &.error {
            color: var(--alert-color);
          }
        }

        .loginInstead {
          padding: 0 0 2.3rem;
          line-height: 2.1rem;
          font-size: 1.6rem;

          button {
            color: var(--primary-color);
          }
        }

        .formFieldItem + .formFieldItem {
          padding: 1.7rem 0 0;
        }

        .formFieldItem {
          :global(#firstName, #lastName) {
            text-transform: capitalize;
          }
        }

        .passwordRequirements {
          max-width: 32rem;
          padding: 1.4rem 0 2.7rem;
          text-align: center;
          line-height: 1.9rem;
          font-size: 1.4rem;
          font-style: italic;
        }

        .terms {
          padding: 0 0 1.4rem;
          line-height: 1.9rem;
          text-align: center;
          font-size: 1.4rem;

          button {
            color: var(--primary-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            padding: 1.6rem 2rem 4.1rem;
          }

          .title {
            padding: 0 0 0.5rem;
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .loginInstead {
            padding: 0 0 1.6rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }

          :global(.signup) {
            position: fixed;
            width: calc(100% - 2.8rem);
            bottom: 2.1rem;
          }
        }
      `}</style>
    </form>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  for (const field of Object.keys(state)) {
    if (isFieldEmpty(state[field])) {
      isValid = false
      errors[field] = 'Required'
    }

    if (re[field]) {
      if (!isFieldValid(field, state)) {
        isValid = false
        errors[field] = validationErrors[field]
      }
    }

    if (
      field === FormFields.ConfirmPassword &&
      state.password.value !== state.confirmPassword.value
    ) {
      isValid = false
      errors[field] = validationErrors[field]
    }
  }

  return { isValid, errors }
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (parseInt(error?.code)) {
      case APIErrors.EmailAlreadyTaken:
        mappedErrors[FormFields.Email] = error.message
        break

      case APIErrors.PhoneNumberNotValid:
      case APIErrors.InvalidPhoneNumberFormat:
        mappedErrors[FormFields.PhoneNumber] = error.message
        break

      case APIErrors.ZipCodeDoesNotExists:
      case APIErrors.InvalidGoogleApiResponseStatusCode:
      case APIErrors.InvalidZipCode:
      case APIErrors.TimezoneNotFound:
      case APIErrors.TimezoneNotSupported:
        mappedErrors[FormFields.ZipCode] = error.message
        break

      default:
        mappedErrors['general'] = error.message
        break
    }
  })

  return mappedErrors
}
